import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { getPerformanceTrend, PerformanceTrendPoint, ScoreStatistics, getScoreStatistics } from '../lib/game/sessionService';
import { useTheme } from '../contexts/ThemeContext';

interface GamePerformanceChartProps {
  userId: string;
  gameId?: string;
  chartType?: 'line' | 'bar' | 'pie';
  interval?: 'week' | 'month' | 'year';
  height?: number;
  showScores?: boolean;
}

// Cores para o modo claro (originais)
const LIGHT_COLORS = ['#4f46e5', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#3b82f6'];
// Cores para o modo escuro (ajustar conforme necessário para bom contraste)
const DARK_COLORS = ['#818cf8', '#f87171', '#fbbf24', '#34d399', '#c4b5fd', '#93c5fd'];

const GamePerformanceChart: React.FC<GamePerformanceChartProps> = ({
  userId,
  gameId,
  chartType = 'line',
  interval = 'month',
  height = 300,
  showScores = false
}) => {
  const { theme } = useTheme();
  const [performanceData, setPerformanceData] = useState<PerformanceTrendPoint[]>([]);
  const [scoreStats, setScoreStats] = useState<ScoreStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const CHART_COLORS = theme === 'dark' ? DARK_COLORS : LIGHT_COLORS;
  const AXIS_COLOR = theme === 'dark' ? '#9ca3af' : '#6b7280'; // cinza para texto do eixo
  const GRID_COLOR = theme === 'dark' ? '#374151' : '#e5e7eb'; // cinza para a grade

  useEffect(() => {
    const loadPerformanceData = async () => {
      if (!userId) return;

      setLoading(true);
      setError(null); // Resetar erro
      try {
        const trendData = await getPerformanceTrend(userId, gameId, interval);
        setPerformanceData(trendData);

        if (showScores && gameId) {
          const stats = await getScoreStatistics(userId, gameId);
          setScoreStats(stats);
        } else {
          setScoreStats(null); // Limpar se não for para mostrar scores ou não tiver gameId
        }
      } catch (err) {
        console.error('Erro ao carregar dados de desempenho:', err);
        setError('Não foi possível carregar os dados de desempenho.');
      } finally {
        setLoading(false);
      }
    };

    loadPerformanceData();
  }, [userId, gameId, interval, showScores]);

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className={`animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 ${theme === 'dark' ? 'border-indigo-400' : 'border-indigo-500'}`}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-700'}`}>
        <p>{error}</p>
      </div>
    );
  }

  if (performanceData.length === 0) {
    const message = !gameId 
      ? "Tendência geral de desempenho não disponível. Selecione um jogo para ver estatísticas detalhadas."
      : "Dados insuficientes para exibir o gráfico deste jogo. Registre mais partidas para ver estatísticas aqui.";
    return (
      <div 
        className={`flex items-center justify-center rounded-lg p-4 ${theme === 'dark' ? 'bg-muted' : 'bg-gray-50'}`}
        style={{ height }}
      >
        <p className={`text-center ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-500'}`}>
          {message}
        </p>
      </div>
    );
  }

  const pieData = [
    { name: 'Vitórias', value: performanceData.reduce((acc, point) => acc + point.win_count, 0) },
    { name: 'Derrotas', value: performanceData.reduce((acc, point) => acc + point.loss_count, 0) },
    { name: 'Empates', value: performanceData.reduce((acc, point) => acc + point.draw_count, 0) }
  ].filter(item => item.value > 0);

  const formatPeriod = (period: string) => {
    if (period.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const date = new Date(period + 'T00:00:00'); // Adicionar T00:00:00 para evitar problemas de fuso horário
      return new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
    } else if (period.match(/^\d{4}-\d{2}$/)) {
      const [year, month] = period.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      return new Intl.DateTimeFormat('pt-BR', { month: 'short', year: 'numeric' }).format(date);
    } else {
      return period;
    }
  };

  const tooltipStyle = theme === 'dark' ? { backgroundColor: '#1f2937', color: '#e5e7eb', border: '1px solid #374151' } : {};
  const legendStyle = theme === 'dark' ? { color: '#e5e7eb' } : {};

  return (
    <div className={`${theme === 'dark' ? 'bg-card text-card-foreground' : 'bg-white text-gray-900'} rounded-lg shadow p-4`}>
      <h3 className="text-lg font-medium mb-4">
        {gameId ? 'Desempenho no Jogo' : 'Desempenho Geral'}
      </h3>

      {chartType === 'pie' && (
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={true}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
              nameKey="name"
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [`${value} partidas`, '']}/>
            <Legend wrapperStyle={legendStyle} />
          </PieChart>
        </ResponsiveContainer>
      )}

      {chartType === 'line' && (
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={performanceData}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
            <XAxis 
              dataKey="period" 
              tickFormatter={formatPeriod}
              stroke={AXIS_COLOR}
              tick={{ fill: AXIS_COLOR }}
            />
            <YAxis yAxisId="left" stroke={AXIS_COLOR} tick={{ fill: AXIS_COLOR }}/>
            <YAxis yAxisId="right" orientation="right" domain={[0, 100]} stroke={AXIS_COLOR} tick={{ fill: AXIS_COLOR }}/>
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(value: number, name: string) => {
                if (name === 'win_rate') return [`${value}%`, 'Taxa de Vitória'];
                return [value, name === 'win_count' ? 'Vitórias' : name === 'loss_count' ? 'Derrotas' : 'Empates'];
              }}
              labelFormatter={formatPeriod}
            />
            <Legend 
              wrapperStyle={legendStyle}
              formatter={(value) => {
                return value === 'win_count' ? 'Vitórias' : 
                       value === 'loss_count' ? 'Derrotas' : 
                       value === 'draw_count' ? 'Empates' : 'Taxa de Vitória';
              }}
            />
            <Line yAxisId="left" type="monotone" dataKey="win_count" stroke={CHART_COLORS[0]} activeDot={{ r: 8 }} />
            <Line yAxisId="left" type="monotone" dataKey="loss_count" stroke={CHART_COLORS[1]} />
            <Line yAxisId="left" type="monotone" dataKey="draw_count" stroke={CHART_COLORS[2]} />
            <Line yAxisId="right" type="monotone" dataKey="win_rate" stroke={CHART_COLORS[3]} strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      )}

      {chartType === 'bar' && (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={performanceData}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
            <XAxis 
              dataKey="period" 
              tickFormatter={formatPeriod}
              stroke={AXIS_COLOR}
              tick={{ fill: AXIS_COLOR }}
            />
            <YAxis stroke={AXIS_COLOR} tick={{ fill: AXIS_COLOR }}/>
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(value: number, name: string) => {
                return [value, name === 'win_count' ? 'Vitórias' : name === 'loss_count' ? 'Derrotas' : 'Empates'];
              }}
              labelFormatter={formatPeriod}
            />
            <Legend 
              wrapperStyle={legendStyle}
              formatter={(value) => {
                return value === 'win_count' ? 'Vitórias' : 
                       value === 'loss_count' ? 'Derrotas' : 'Empates';
              }}
            />
            <Bar dataKey="win_count" stackId="a" fill={CHART_COLORS[0]} />
            <Bar dataKey="loss_count" stackId="a" fill={CHART_COLORS[1]} />
            <Bar dataKey="draw_count" stackId="a" fill={CHART_COLORS[2]} />
          </BarChart>
        </ResponsiveContainer>
      )}

      {showScores && scoreStats && scoreStats.score_trend && scoreStats.score_trend.length > 0 && (
        <div className="mt-6">
          <h4 className={`text-md font-medium mb-3 ${theme === 'dark' ? 'text-card-foreground' : 'text-gray-800'}`}>Evolução da Pontuação</h4>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={scoreStats.score_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR}/>
              <XAxis dataKey="date" stroke={AXIS_COLOR} tick={{ fill: AXIS_COLOR }}/>
              <YAxis stroke={AXIS_COLOR} tick={{ fill: AXIS_COLOR }}/>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={legendStyle} />
              <Line type="monotone" dataKey="avg_score" stroke={CHART_COLORS[0]} />
            </LineChart>
          </ResponsiveContainer>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            <div className={`${theme === 'dark' ? 'bg-muted' : 'bg-indigo-50'} p-3 rounded-lg`}>
              <p className={`text-xs mb-1 ${theme === 'dark' ? 'text-muted-foreground' : 'text-indigo-600'}`}>Pontuação Média</p>
              <p className={`text-lg font-semibold ${theme === 'dark' ? 'text-foreground' : 'text-indigo-900'}`}>{scoreStats.avg_score?.toFixed(1) || 'N/A'}</p>
            </div>
            <div className={`${theme === 'dark' ? 'bg-muted' : 'bg-green-50'} p-3 rounded-lg`}>
              <p className={`text-xs mb-1 ${theme === 'dark' ? 'text-muted-foreground' : 'text-green-600'}`}>Maior Pontuação</p>
              <p className={`text-lg font-semibold ${theme === 'dark' ? 'text-foreground' : 'text-green-900'}`}>{scoreStats.max_score || 'N/A'}</p>
            </div>
            <div className={`${theme === 'dark' ? 'bg-muted' : 'bg-blue-50'} p-3 rounded-lg`}>
              <p className={`text-xs mb-1 ${theme === 'dark' ? 'text-muted-foreground' : 'text-blue-600'}`}>Média em Vitórias</p>
              <p className={`text-lg font-semibold ${theme === 'dark' ? 'text-foreground' : 'text-blue-900'}`}>{scoreStats.win_score_avg?.toFixed(1) || 'N/A'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GamePerformanceChart; 